import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useDialog, useModal} from '@hooks';
import {workdayLabelMap} from '@lib/logic';
import {
  getBuildingById,
  selectBuilding,
  selectLocationFromBuildingId,
  selectTimeslot,
  useAppDispatch,
  useAppSelector,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {trimWorkspaceReservations} from '@lib/store/src/data/timeslots/trimming/trimmingActions';
import {
  CancellableWorkdayStatus,
  CreateWorkdayDto,
  LocalDateTimeString,
  UpdateWorkdayDto,
  Workday,
} from '@lib/store/src/data/timeslots/apiTypes';
import {MAX_TIMESPAN_PRESET, MIN_TIMESPAN_PRESET} from '@lib/store/src/data/timeslots/constants/startAndEndTimes';
import {
  dateKeyForDateTimeString,
  localDateTimeStringToHHMM,
  timeLabel,
} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {
  getConfirmedWorkspaceReservationsForWorkday,
  getFirstAvailableWorkdayTimeslot,
  getFirstAvailableWorkspaceReservationTimeslot,
  getIsOfficeDayBookableBasedOnQuota,
  getScheduleForDateIsUpdating,
  getWorkdaysForDate,
} from '@lib/store/src/data/timeslots/selectors';
import {TimeSlotsTimePicker} from '@molecules';
import {Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from '@ui';
import {StyledModalPage} from 'src/components/organisms/bookWorkspace/BookWorkspaceCard/styles';
import {DaySchedule} from 'src/components/organisms/ganttChart/DaySchedule';
import {WorkdayDetailsModalBottom} from './WorkdayDetailsModalBottom';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {ScheduleMutation, useDaySchedule} from '@lib/store/src/ui/hooks/timeslots/useDaySchedule';
import {
  getBuildingIdsThatRequireWorkspaceBooking,
  getBuildingSections,
} from '@lib/store/src/data/timeslots/selectors/getBuildingSections';
import {BookWorkspaceCard} from 'src/components/organisms/bookWorkspace';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {groupBy} from '@lib/utils';

interface Props {
  workday?: Workday;
  selectedDate: string;
}

const statuses: Array<CancellableWorkdayStatus> = ['OfficeDay', 'WorkingRemotely', 'NotWorking', 'Unknown'];

type FormState = {
  id?: string;
  status: CancellableWorkdayStatus;
  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;
  buildingId?: string;
};

export const WorkdayDetailsModal = ({workday, selectedDate}: Props) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {addPages, closeModal} = useModal();
  const {alertDialog, destructiveDialog, confirmationDialog} = useDialog();

  const date = useMemoDate(selectedDate);

  const isNewWorkday = workday === undefined;

  const isOfficeDayBookableBasedOnQuota = useAppSelector(getIsOfficeDayBookableBasedOnQuota(date));
  const firstAvailableWorkdayTimeslot = useAppSelector(getFirstAvailableWorkdayTimeslot(selectedDate));
  const firstAvailableWorkspaceReservationTimeslot = useAppSelector(
    getFirstAvailableWorkspaceReservationTimeslot(workday),
  );
  const workdays = useAppSelector(getWorkdaysForDate(dateKeyForDateTimeString(selectedDate)));
  const currentWorkdayConfirmedWorkspaceReservations = useAppSelector((state) =>
    workday ? getConfirmedWorkspaceReservationsForWorkday(workday.id)(state) : [],
  );
  const currentWorkdayHasConfirmedWorkspaceReservations = currentWorkdayConfirmedWorkspaceReservations.length > 0;

  const [isEditingLocally, setIsEditingLocally] = useState(isNewWorkday);
  const [formState, setFormState] = useState<FormState>(() =>
    makeInitialFormState(workday, selectedDate, firstAvailableWorkdayTimeslot, isOfficeDayBookableBasedOnQuota),
  );

  const mutating = useAppSelector(getScheduleForDateIsUpdating(selectedDate));
  const buildingSections = useAppSelector((state) => getBuildingSections(state, selectedDate, workday));
  const buildingIdsWithRequiredWorkspaceBooking = useAppSelector(getBuildingIdsThatRequireWorkspaceBooking);
  const workdayBuilding = useAppSelector((state) =>
    formState.buildingId ? getBuildingById(state, formState.buildingId) : undefined,
  );

  const officeDayCount = useMemo(() => {
    return workdays?.data?.filter((wd) => isOfficeWorkday(wd)).length ?? 0;
  }, [workdays?.data]);

  const mutation = useMemo<ScheduleMutation | undefined>(() => {
    if (!isEditingLocally) return undefined;

    return {
      type: workday ? 'EDIT' : 'CREATE',
      entityType: formState.status === 'OfficeDay' ? 'OFFICE_WORKDAY_STATUS' : 'REMOTE_WORKDAY_STATUS',
      entity: {
        id: formState.id ?? 'DRAFT',
        localStartTime: formState.localStart,
        localEndTime: formState.localEnd,
      },
    };
  }, [isEditingLocally, workday, formState]);

  const vm = useDaySchedule(selectedDate, mutation);

  // Form state
  const setStatus = (status: FormState['status']) => setFormState((prev) => ({...prev, status}));
  const setBuilding = (buildingId: string) => setFormState((prev) => ({...prev, buildingId}));
  const setStart = (time: string) =>
    setFormState((prev) => ({...prev, localStart: combineDateAndTime(selectedDate, time)}));
  const setEnd = (time: string) =>
    setFormState((prev) => ({...prev, localEnd: combineDateAndTime(selectedDate, time)}));

  const confirmCancellation = useCallback(
    () =>
      destructiveDialog(
        t('workplace:DeleteYourBookings'),
        t('workplace:DeleteYourBookingsMessage', {
          buildingName: workday?.status === 'OfficeDay' ? workday.buildingName : undefined,
        }),
        'reportProblem',
      ),
    [t, workday, destructiveDialog],
  );

  const deleteWorkday = useCallback(async () => {
    if (!workday) return;

    if (isOfficeWorkday(workday)) {
      // Prompt user to delete workspace reservations if they exist
      if (currentWorkdayHasConfirmedWorkspaceReservations) {
        const confirmed = await confirmCancellation();
        if (!confirmed) return;
      }

      // only delete parking lot reservations if there is 1+ office days left otherwise delete everything for the given workday
      // TODO: Properly check if any of the office days have access to the parking lot
      if (officeDayCount > 1) {
        await dispatch(actions.cancelWorkspaceReservationsOnWorkday({workday}));
        await dispatch(withAsyncThunkErrorHandling(() => actions.cancelWorkday(workday)));
      } else {
        await dispatch(actions.cancelWorkdayIncludingReservations({workday}));
      }
    } else {
      await dispatch(withAsyncThunkErrorHandling(() => actions.cancelWorkday(workday)));
    }

    closeModal();
  }, [
    workday,
    closeModal,
    currentWorkdayHasConfirmedWorkspaceReservations,
    officeDayCount,
    confirmCancellation,
    dispatch,
  ]);

  const saveWorkday = useCallback(async () => {
    let hasReservations = currentWorkdayHasConfirmedWorkspaceReservations;

    // 1. Workday cancelled altogether
    if (formState.status === 'Unknown') {
      if (workday) {
        await deleteWorkday();
      } else {
        // Nothing to save, just close
        closeModal();
      }

      return;
    }

    // 2. Switching away from office day
    const switchingAwayFromOfficeDay = workday?.status === 'OfficeDay' && formState.status !== 'OfficeDay';

    if (switchingAwayFromOfficeDay && hasReservations) {
      const confirmed = await confirmCancellation();
      if (!confirmed) return;

      await dispatch(actions.cancelWorkspaceReservationsOnWorkday({workday}));

      hasReservations = false;
    }

    const input = toCreateOrUpdateInput(formState);

    // 3. Update or create (confirm that workday still exists)
    const isEditingWorkday = 'id' in input && workdays?.data?.some((wd) => wd.id === input.id);

    if (isEditingWorkday && isOfficeWorkday(workday) && input.status === 'OfficeDay') {
      const buildingChanged = input.buildingId !== workday.buildingId;
      // 3a. Editing same workday but the building changed, so cancel reservations if needed

      if (buildingChanged && hasReservations) {
        const confirmed = await confirmCancellation();
        if (!confirmed) return;

        await dispatch(actions.cancelWorkspaceReservationsOnWorkday({workday}));

        hasReservations = false;
      }

      // 3b. Handle any auto-trimming that might be needed
      if (!buildingChanged && vm.schedule?.invalidIds.size) {
        const {valid = [], invalid = []} = groupBy(currentWorkdayConfirmedWorkspaceReservations, (r) =>
          vm.schedule.invalidIds.has(r.id) ? 'invalid' : 'valid',
        );

        if (invalid.length > 0) {
          const resolved = await dispatch(
            trimWorkspaceReservations({
              workday: input,
              workspaceReservations: invalid,
              onConfirmCancellations: () =>
                confirmationDialog(
                  t('timeslots:CancelWorkspaceReservationsTitle'),
                  t('timeslots:CancelWorkspaceReservationsDescription'),
                ),
              onInvalidCancellation:
                buildingIdsWithRequiredWorkspaceBooking.has(input.buildingId) && valid.length === 0
                  ? () => {
                      alertDialog(
                        t('workplace:WorkspaceBookingRequired'),
                        t('timeslots:WorkspaceBookingsAreMandatory'),
                        undefined,
                        t('translation:GotIt'),
                      );
                    }
                  : undefined,
            }),
          );

          if (!resolved) return;
        }
      }
    }

    // 4. Save
    const action = isEditingWorkday ? actions.updateWorkday(input) : actions.createWorkday(input);
    const {result, success} = await dispatch(withAsyncThunkErrorHandling(() => action));

    // 5. Enter reservation flow if mandatory
    if (success) {
      const needsReservationFlow =
        isOfficeWorkday(result) && buildingIdsWithRequiredWorkspaceBooking.has(result.buildingId) && !hasReservations;

      if (needsReservationFlow) {
        dispatch(
          selectTimeslot(
            firstAvailableWorkspaceReservationTimeslot ?? {
              startTime: result.localStart,
              endTime: result.localEnd,
            },
          ),
        );
        dispatch(selectLocationFromBuildingId(result.buildingId));

        addPages([<BookWorkspaceCard workday={result} />]);

        return;
      }

      // ensure the building selection state is in sync with the workday building
      isOfficeWorkday(result) && dispatch(selectBuilding(result.buildingId));

      closeModal();
    }
  }, [
    addPages,
    alertDialog,
    confirmCancellation,
    confirmationDialog,
    currentWorkdayConfirmedWorkspaceReservations,
    currentWorkdayHasConfirmedWorkspaceReservations,
    deleteWorkday,
    dispatch,
    buildingIdsWithRequiredWorkspaceBooking,
    firstAvailableWorkspaceReservationTimeslot,
    closeModal,
    formState,
    t,
    vm.schedule?.invalidIds,
    workday,
    workdays?.data,
  ]);

  const handleCancel = useCallback(() => {
    if (isEditingLocally) {
      setIsEditingLocally(false);
      setFormState(
        makeInitialFormState(workday, selectedDate, firstAvailableWorkdayTimeslot, isOfficeDayBookableBasedOnQuota),
      );
    } else {
      closeModal();
    }
  }, [
    closeModal,
    isEditingLocally,
    selectedDate,
    workday,
    firstAvailableWorkdayTimeslot,
    isOfficeDayBookableBasedOnQuota,
  ]);

  const startHhmm = hhmm(formState.localStart);
  const endHhmm = hhmm(formState.localEnd);

  const isSavingDisabled = useMemo(() => {
    const validTime = isStartBeforeOrEqual(startHhmm, endHhmm);
    const needsBuilding = formState.status === 'OfficeDay';

    return vm.isInvalid || !validTime || (needsBuilding && !formState.buildingId);
  }, [endHhmm, formState.buildingId, formState.status, startHhmm, vm.isInvalid]);

  return (
    <StyledModalPage
      title={t('workplace:YourLocation')}
      bottom={
        <WorkdayDetailsModalBottom
          workday={workday}
          isEditing={isEditingLocally}
          setIsEditing={setIsEditingLocally}
          isMutating={mutating}
          isSavingDisabled={isSavingDisabled}
          onDelete={deleteWorkday}
          onSave={saveWorkday}
          onCancel={handleCancel}
        />
      }
      onClose={closeModal}>
      <div className="p-5 pt-0">
        {/* Status row */}
        <div className="flex items-center justify-between gap-2 border-b h-20">
          <p className="font-semibold">{t('timeslots:WorkdayLocation')}</p>

          {isNewWorkday || isEditingLocally ? (
            <div className="w-52">
              <Select
                value={formState.status}
                onValueChange={setStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem
                      key={status}
                      value={status}
                      disabled={status === 'OfficeDay' && !isOfficeDayBookableBasedOnQuota}>
                      {t(workdayLabelMap[status])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-mapiq-black-500">{t(workdayLabelMap[formState.status])}</p>
          )}
        </div>

        {/* Building row */}
        {formState.status === 'OfficeDay' && (
          <div className="flex items-center justify-between gap-2 border-b h-20">
            <p className="font-semibold">{t('translation:Building')}</p>

            {isEditingLocally ? (
              <div className="w-52">
                <Select
                  value={formState.buildingId ?? ''}
                  onValueChange={setBuilding}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {buildingSections.open.map((building) => (
                        <SelectItem
                          key={building.id}
                          value={building.id}>
                          {building.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>

                    <SelectGroup>
                      {buildingSections.closed.map((building) => (
                        <SelectItem
                          key={building.id}
                          value={building.id}
                          disabled>
                          {building.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-mapiq-black-500">{workdayBuilding?.name ?? t('timeslots:Select')}</p>
            )}
          </div>
        )}

        {/* Timeslot row */}
        {formState.status !== 'Unknown' && (
          <>
            <div className="flex items-center justify-between gap-2 border-b h-20">
              <p className="font-semibold">{t('timeslots:Time')}</p>

              {isEditingLocally ? (
                <div className="flex items-center gap-2">
                  <TimeSlotsTimePicker
                    defaultValue={startHhmm}
                    onChange={(v) => {
                      const next = v || '00:00';

                      setStart(next);
                    }}
                    hasError={(vm.schedule && vm.schedule.validationIssues.length > 0) || startHhmm >= endHhmm}
                    presetTransformer={(o) => ({
                      ...o,
                      hidden: o.id < MIN_TIMESPAN_PRESET || o.id > MAX_TIMESPAN_PRESET,
                    })}
                  />
                  -
                  <TimeSlotsTimePicker
                    defaultValue={endHhmm}
                    onChange={(v) => setEnd(v || '23:59')}
                    hasError={(vm.schedule && vm.schedule.validationIssues.length > 0) || endHhmm <= startHhmm}
                    presetTransformer={(o) => {
                      return {
                        ...o,
                        hidden:
                          o.id < MIN_TIMESPAN_PRESET ||
                          o.id > MAX_TIMESPAN_PRESET ||
                          (!!startHhmm && o.id <= startHhmm),
                      };
                    }}
                  />
                </div>
              ) : (
                <p className="text-mapiq-black-500">{timeLabel(formState.localStart, formState.localEnd, t)}</p>
              )}
            </div>
          </>
        )}

        {/* Gantt chart preview */}
        <div className="mt-4">
          <DaySchedule
            vm={vm}
            mutation={mutation}
            extraArgs={{
              buildingName: workdayBuilding?.name,
              workspaceReservationCount: currentWorkdayConfirmedWorkspaceReservations.filter((r) =>
                vm.schedule?.invalidIds.has(r.id),
              ).length,
            }}
          />
        </div>
      </div>
    </StyledModalPage>
  );
};

const combineDateAndTime = (date: string, time: string): LocalDateTimeString => {
  return `${date}T${time}` as LocalDateTimeString;
};

const hhmm = (dt: LocalDateTimeString) => {
  return localDateTimeStringToHHMM(dt);
};

const isStartBeforeOrEqual = (startHHMM: string, endHHMM: string) => {
  return startHHMM <= endHHMM;
};

const makeInitialFormState = (
  workday: Workday | undefined,
  selectedDate: string,
  firstAvailableWorkdayTimeslot:
    | {
        startTime: string;
        endTime: string;
      }
    | undefined,
  isOfficeDayBookable: boolean,
): FormState => {
  if (!workday) {
    const status = isOfficeDayBookable ? 'OfficeDay' : 'WorkingRemotely';

    return {
      status,
      localStart: firstAvailableWorkdayTimeslot?.startTime ?? combineDateAndTime(selectedDate, '09:00'),
      localEnd: firstAvailableWorkdayTimeslot?.endTime ?? combineDateAndTime(selectedDate, '17:00'),
    };
  }

  const base: FormState = {
    id: workday.id,
    status: isOfficeWorkday(workday) ? 'OfficeDay' : workday.status,
    localStart: workday.localStart,
    localEnd: workday.localEnd,
  };

  if (isOfficeWorkday(workday)) {
    base.buildingId = workday.buildingId;
  }

  return base;
};

const toCreateOrUpdateInput = (fs: FormState): CreateWorkdayDto | UpdateWorkdayDto => {
  if (fs.status === 'Unknown') {
    throw new Error('Invalid call: toCreateOrUpdateInput should not be used with status "Unknown".');
  }

  if (fs.status === 'OfficeDay') {
    const base = {
      status: 'OfficeDay' as const,
      localStart: fs.localStart,
      localEnd: fs.localEnd,
      buildingId: fs.buildingId!,
    };

    return fs.id ? {...base, id: fs.id} : base;
  } else {
    const base = {
      status: fs.status,
      localStart: fs.localStart,
      localEnd: fs.localEnd,
    };

    return fs.id ? {...base, id: fs.id} : base;
  }
};
